import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { isSafeRelativePath } from '@/utils/return-url';

type CallbackStatus = 'processing' | 'success' | 'error';

function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [error, setError] = useState<string | null>(null);
  const [redirectTo, setRedirectTo] = useState<string>('/dashboard');

  useEffect(() => {
    try {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      const token = hashParams.get('token');
      const authError = hashParams.get('authError');

      if (authError) {
        const errorMsg = authError.includes('Contact admin')
          ? 'Access denied. Please contact the administrator for access.'
          : `Authentication error: ${authError}`;
        throw new Error(errorMsg);
      }

      if (!token) {
        throw new Error('No authentication token received');
      }

      const queryParams = new URLSearchParams(window.location.search);
      const returnUrl = queryParams.get('returnUrl');
      if (isSafeRelativePath(returnUrl)) {
        // oxlint-disable-next-line react/set-state-in-effect
        setRedirectTo(returnUrl);
      }

      localStorage.setItem('authToken', token);
      setStatus('success');
    } catch (err) {
      console.error('Auth callback error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setStatus('error');
    }
  }, []);

  if (status === 'processing') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Completing sign in...</h2>
          <p className="text-gray-600">Please wait while we authenticate you.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return <Navigate to={redirectTo} replace />;
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="mx-auto max-w-md p-6 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
            <svg className="size-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="mb-3 text-2xl font-semibold text-gray-900">Sign in failed</h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="rounded-md bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export { AuthCallback };
